import React, { useRef, useState } from "react";
import { Button, Badge } from "react-bootstrap";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { NEW_FIELD_DRAG_TYPE } from "./FormFieldToolbox";

// Drag payload MIME type for reordering/moving an EXISTING field — carries
// {sectionIndex, fieldIndex} so a drop handler can tell a "new field from
// toolbox" drop (NEW_FIELD_DRAG_TYPE) apart from "move this existing field"
// (this one), even though both can land on the same drop target.
const MOVE_FIELD_DRAG_TYPE = "application/x-form-builder-move-field";
const MOVE_SECTION_DRAG_TYPE = "application/x-form-builder-move-section";

function FieldCard({ field, sectionIndex, fieldIndex, selected, onSelect, onDuplicate, onRemove, dragProps, errors, readOnly }) {
  const hasError = !!errors?.length;
  return (
    <div
      className={`form-canvas-field-card${selected ? " is-selected" : ""}${hasError ? " has-error" : ""}`}
      onClick={() => onSelect(sectionIndex, fieldIndex)}
      {...dragProps}
    >
      <div className="d-flex align-items-center gap-2 w-100">
        {/* Only shown when actually draggable -- otherwise this icon is a
            false affordance: a published/archived version's fields aren't
            draggable={true} at all (see `dragProps` below), so a visible
            drag handle here would invite a drag that silently does
            nothing. */}
        {!readOnly && (
          <span className="form-canvas-field-handle" title="Drag to reorder or move to another section" aria-hidden="true">
            <DragIndicatorIcon fontSize="small" />
          </span>
        )}
        <div className="form-canvas-field-body">
          <div className="form-canvas-field-label">{field.label || <span className="text-muted">(untitled field)</span>}</div>
          <div className="form-canvas-field-badges">
            <Badge bg="light" text="dark">{field.field_type}</Badge>
            {!!field.is_required && <Badge bg="danger">Required</Badge>}
            {!!field.score_enabled && <Badge bg="info" text="dark">Scored</Badge>}
          </div>
        </div>
        <div className="form-canvas-field-actions">
          <button
            type="button"
            className="form-canvas-icon-btn"
            title={readOnly ? "Published versions cannot be modified." : "Edit properties"}
            aria-label="Edit field properties"
            onClick={(e) => { e.stopPropagation(); onSelect(sectionIndex, fieldIndex); }}
          >
            <EditOutlinedIcon fontSize="small" />
          </button>
          <button
            type="button"
            className="form-canvas-icon-btn"
            title={readOnly ? "Published versions cannot be modified." : "Duplicate field"}
            aria-label="Duplicate field"
            disabled={readOnly}
            onClick={(e) => { e.stopPropagation(); if (!readOnly) onDuplicate(sectionIndex, fieldIndex); }}
          >
            <ContentCopyOutlinedIcon fontSize="small" />
          </button>
          <button
            type="button"
            className="form-canvas-icon-btn form-canvas-icon-btn-danger"
            title={readOnly ? "Published versions cannot be modified." : "Delete field"}
            aria-label="Delete field"
            disabled={readOnly}
            onClick={(e) => { e.stopPropagation(); if (!readOnly) onRemove(sectionIndex, fieldIndex); }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </button>
        </div>
      </div>
      {hasError && (
        <div className="form-canvas-field-error">
          <WarningAmberIcon fontSize="inherit" aria-hidden="true" />
          <span>{errors[0].message}</span>
        </div>
      )}
    </div>
  );
}

// Center panel — every section shown vertically, each holding its own
// field cards. Handles three drag scenarios: reordering sections, moving/
// reordering fields within or across sections, and dropping a brand-new
// field type from the toolbox. Native HTML5 DnD only (see FormBuilderPage
// header comment for why no library was added).
export default function FormCanvas({
  sections,
  selectedSectionIndex,
  selectedFieldIndex,
  collapsedSections,
  onToggleCollapse,
  onSelectField,
  onSelectSection,
  onAddSection,
  onReorderSections,
  onDuplicateSection,
  onRemoveSection,
  onAddFieldAt,
  onMoveField,
  onDuplicateField,
  onRemoveField,
  readOnly,
  fieldErrorMap,
}) {
  const dragSectionRef = useRef(null);
  const dragFieldRef = useRef(null);
  const [sectionDragOver, setSectionDragOver] = useState(null);
  const [fieldDropTarget, setFieldDropTarget] = useState(null); // { sectionIndex, fieldIndex }

  const handleSectionDragStart = (index) => (e) => {
    dragSectionRef.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(MOVE_SECTION_DRAG_TYPE, String(index));
  };
  const handleSectionDragOver = (index) => (e) => {
    if (!e.dataTransfer.types.includes(MOVE_SECTION_DRAG_TYPE)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (sectionDragOver !== index) setSectionDragOver(index);
  };
  const handleSectionDrop = (index) => (e) => {
    if (!e.dataTransfer.types.includes(MOVE_SECTION_DRAG_TYPE)) return;
    e.preventDefault();
    e.stopPropagation();
    const from = dragSectionRef.current;
    setSectionDragOver(null);
    dragSectionRef.current = null;
    if (from == null || from === index) return;
    onReorderSections(from, index);
  };

  const handleFieldDragStart = (sectionIndex, fieldIndex) => (e) => {
    dragFieldRef.current = { sectionIndex, fieldIndex };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(MOVE_FIELD_DRAG_TYPE, JSON.stringify({ sectionIndex, fieldIndex }));
    e.stopPropagation();
  };

  // A field-list drop zone accepts EITHER a new-field-from-toolbox drop
  // (copy) or an existing-field move (move); both resolve to the same
  // "insert at this position" outcome.
  const handleFieldZoneDragOver = (sectionIndex, fieldIndex) => (e) => {
    const isNew = e.dataTransfer.types.includes(NEW_FIELD_DRAG_TYPE);
    const isMove = e.dataTransfer.types.includes(MOVE_FIELD_DRAG_TYPE);
    if (!isNew && !isMove) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = isNew ? "copy" : "move";
    const next = { sectionIndex, fieldIndex };
    if (fieldDropTarget?.sectionIndex !== sectionIndex || fieldDropTarget?.fieldIndex !== fieldIndex) {
      setFieldDropTarget(next);
    }
  };

  const handleFieldZoneDrop = (sectionIndex, fieldIndex) => (e) => {
    const newType = e.dataTransfer.getData(NEW_FIELD_DRAG_TYPE);
    const movePayload = e.dataTransfer.getData(MOVE_FIELD_DRAG_TYPE);
    if (!newType && !movePayload) return;
    e.preventDefault();
    e.stopPropagation();
    setFieldDropTarget(null);
    dragFieldRef.current = null;
    if (newType) {
      onAddFieldAt(sectionIndex, newType, fieldIndex);
    } else if (movePayload) {
      const from = JSON.parse(movePayload);
      onMoveField(from.sectionIndex, from.fieldIndex, sectionIndex, fieldIndex);
    }
  };

  const clearDragState = () => {
    dragSectionRef.current = null;
    dragFieldRef.current = null;
    setSectionDragOver(null);
    setFieldDropTarget(null);
  };

  return (
    <div className="form-canvas">
      <div className="form-canvas-toolbar">
        <strong>Sections</strong>
        {!readOnly && <Button size="sm" onClick={onAddSection}>+ Add Section</Button>}
      </div>

      {sections.map((section, sectionIndex) => {
        const fields = section.fields || [];
        const collapsed = !!collapsedSections?.[section.id];
        return (
          <div
            key={section.id}
            className={`form-canvas-section${sectionDragOver === sectionIndex ? " is-drag-over" : ""}${selectedSectionIndex === sectionIndex && selectedFieldIndex == null ? " is-selected" : ""}`}
            draggable={!readOnly}
            onDragStart={readOnly ? undefined : handleSectionDragStart(sectionIndex)}
            onDragOver={readOnly ? undefined : handleSectionDragOver(sectionIndex)}
            onDrop={readOnly ? undefined : handleSectionDrop(sectionIndex)}
            onDragEnd={clearDragState}
          >
            <div className="form-canvas-section-header" onClick={() => onSelectSection(sectionIndex)}>
              {!readOnly && (
                <span className="form-canvas-field-handle" title="Drag to reorder section" aria-hidden="true">
                  <DragIndicatorIcon fontSize="small" />
                </span>
              )}
              <button
                type="button"
                className="form-canvas-icon-btn"
                title={collapsed ? "Expand section" : "Collapse section"}
                aria-label={collapsed ? "Expand section" : "Collapse section"}
                onClick={(e) => { e.stopPropagation(); onToggleCollapse(section.id); }}
              >
                {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
              </button>
              <span className="form-canvas-section-title">{section.title || "(untitled section)"}</span>
              <Badge bg="light" text="dark">{fields.length} field{fields.length === 1 ? "" : "s"}</Badge>
              {section.score_enabled && <Badge bg="info" text="dark">Scored</Badge>}
              {section.score_enabled && section.weightage != null && section.weightage !== "" && (
                <Badge bg="light" text="dark" className="form-canvas-weightage-badge">{section.weightage}%</Badge>
              )}
              <span className="flex-grow-1" />
              {!readOnly && (
                <>
                  <button type="button" className="form-canvas-icon-btn" title="Duplicate section" aria-label="Duplicate section" onClick={(e) => { e.stopPropagation(); onDuplicateSection(sectionIndex); }}>
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </button>
                  <button type="button" className="form-canvas-icon-btn form-canvas-icon-btn-danger" title="Delete section" aria-label="Delete section" onClick={(e) => { e.stopPropagation(); onRemoveSection(sectionIndex); }}>
                    <DeleteOutlineIcon fontSize="small" />
                  </button>
                </>
              )}
            </div>

            {!collapsed && (
              <div
                className="form-canvas-field-list"
                data-testid={`form-canvas-field-list-${section.id}`}
                onDragOver={readOnly ? undefined : handleFieldZoneDragOver(sectionIndex, fields.length)}
                onDrop={readOnly ? undefined : handleFieldZoneDrop(sectionIndex, fields.length)}
              >
                {fields.map((field, fieldIndex) => (
                  <div
                    key={field.id}
                    onDragOver={readOnly ? undefined : handleFieldZoneDragOver(sectionIndex, fieldIndex)}
                    onDrop={readOnly ? undefined : handleFieldZoneDrop(sectionIndex, fieldIndex)}
                    className={fieldDropTarget?.sectionIndex === sectionIndex && fieldDropTarget?.fieldIndex === fieldIndex ? "is-field-drop-target" : undefined}
                  >
                    <FieldCard
                      field={field}
                      sectionIndex={sectionIndex}
                      fieldIndex={fieldIndex}
                      selected={selectedSectionIndex === sectionIndex && selectedFieldIndex === fieldIndex}
                      onSelect={onSelectField}
                      onDuplicate={onDuplicateField}
                      onRemove={onRemoveField}
                      errors={fieldErrorMap?.[field.field_key]}
                      readOnly={readOnly}
                      dragProps={readOnly ? {} : {
                        draggable: true,
                        onDragStart: handleFieldDragStart(sectionIndex, fieldIndex),
                        onDragEnd: clearDragState,
                      }}
                    />
                  </div>
                ))}
                {!fields.length && (
                  <div className="form-canvas-empty-drop">Drag a field here from the toolbox, or click one to add it.</div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {!sections.length && <div className="text-muted small p-3">No sections yet — click "+ Add Section" to begin.</div>}
    </div>
  );
}
